import React from 'react'

import { GetPreviewDetailsQuery } from '@sketch/gql-types'
import { isPreviewPageCanvasRoute, routes } from '@sketch/modules-common'

import {
  Copy,
  HeaderListItem,
  HeaderSection,
  LinkListPrototypeCard,
  LinkListPrototypeCards,
  LinkSection,
  PageIcon,
  PrototypeIcon,
  StyledDropdownNavLink,
} from './AboutTab.styles'
import { DocumentNameWrapper } from '../EditableDocumentName/EditableDocumentName.styles'
import { DocumentName } from '../../../workspace/pages/WorkspaceSettingsDocumentPreviews/DocumentPreviewsLayout.styles'
import { DocumentDescriptionWrapper } from '../EditableDocumentDescription/EditableDocumentDescription.styles'
import { MarkdownDocumentDescription } from '../EditableDocumentDescription'
import { useRouteMatch } from 'react-router'
import PrototypeCard from '../PrototypeCard'
import { Tooltip } from '@sketch/components'
import { getFrameThumbnail } from '../../utils'
import styled from 'styled-components'

const StyledHeaderSection = styled(HeaderSection)`
  padding-bottom: 8px;
`

const StyledLinkSection = styled(LinkSection)`
  margin-bottom: 10px; /* stylelint-disable-line scales/space */
`

interface AboutPreviewTab {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
}

const AboutPreviewTab: React.FCC<AboutPreviewTab> = ({ preview }) => {
  const { path } = useRouteMatch()

  const { name, description, pages, prototypes } = preview
  const hasPrototypes = prototypes.entries.length > 0
  const hasPages = pages.entries.length > 0

  return (
    <>
      <StyledHeaderSection data-testid="about-tab-header">
        <Copy data-testid="document-type">Preview</Copy>
        <DocumentNameWrapper data-testid="document-name">
          <DocumentName>{name}</DocumentName>
        </DocumentNameWrapper>

        {description && (
          <DocumentDescriptionWrapper data-testid="document-description">
            <MarkdownDocumentDescription>
              {description}
            </MarkdownDocumentDescription>
          </DocumentDescriptionWrapper>
        )}
      </StyledHeaderSection>

      {(hasPages || hasPrototypes) && (
        <StyledLinkSection>
          <HeaderListItem>Views</HeaderListItem>
          {hasPages && (
            <StyledDropdownNavLink
              key={'pages'}
              to={routes.PREVIEW_VIEW.create({
                previewID: preview.identifier,
              })}
              isActive={() => isPreviewPageCanvasRoute(path)}
            >
              <PageIcon />
              Pages
            </StyledDropdownNavLink>
          )}

          {hasPrototypes && (
            <StyledDropdownNavLink
              key={'prototypes'}
              to={routes.PREVIEW_PROTOTYPES.create({
                previewID: preview.identifier,
              })}
            >
              <PrototypeIcon />
              Prototypes
            </StyledDropdownNavLink>
          )}

          {hasPrototypes && (
            <LinkListPrototypeCards>
              {prototypes.entries.slice(0, 3).map(frame => (
                <LinkListPrototypeCard key={frame.uuid}>
                  <Tooltip
                    content={frame.name}
                    placement="bottom"
                    disableWhenTouchDevice
                  >
                    <PrototypeCard
                      artboardUUID={frame.uuid}
                      title={frame.name}
                      src={getFrameThumbnail(frame)}
                      showTitle={false}
                      showDropdown={false}
                      iconSize={32}
                    />
                  </Tooltip>
                </LinkListPrototypeCard>
              ))}
            </LinkListPrototypeCards>
          )}
        </StyledLinkSection>
      )}
    </>
  )
}

export default AboutPreviewTab
